import React from 'react'
import { useIntl } from 'react-intl'

import { SectionCurve } from '../../../components/SectionCurve'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { SectionTestimonials } from '../../home/SectionTestimonials'
import { Section3CardsBenefits } from '../Section3CardsBenefits'
import { SectionGridBenefits } from '../SectionGridBenefits'
import { SectionHero } from '../SectionHero'
import { SectionQuote } from '../SectionQuote'
import { SectionTable } from '../SectionTable'

export default function CompareToGoogleDocsPage() {
  const routes = useRoutes()
  const intl = useIntl()

  const title = intl.formatMessage({
    defaultMessage:
      'Slite – A Google Docs alternative that keeps teams organised',
    id: 'compare.googleDocs.page.seo.title',
  })

  const description = intl.formatMessage({
    defaultMessage: `Slite makes it easy to organise docs, share knowledge and track key decisions with your remote team.`,
    id: 'compare.googleDocs.page.seo.description',
  })

  return (
    <SlitePage
      canonicalUrl={routes.compare.detail({
        slug: 'alternative-to-google-docs',
      })}
      background="moon-dust"
      title={title}
      description={description}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/b1da10e287a7aa408927d5af376a4e0e9eecce3d-2100x1200.webp"
    >
      <SectionHero
        competitorName="Google Docs"
        title={intl.formatMessage({
          defaultMessage:
            'A Google Docs alternative that keeps teams organised.',
          id: 'compare.googleDocs.page.title',
        })}
        subtitle={intl.formatMessage({
          defaultMessage:
            'Slite makes it easy to organise docs, share knowledge and track key decisions with your remote team, without the chaos of folders, scattered files, and "where is that doc?" searches.',
          id: 'compare.googleDocs.page.subtitle',
        })}
      />

      <SectionCurve from="moon-dust" to="shade-100" shape="top-large" />

      <Section3CardsBenefits
        title={intl.formatMessage(
          {
            defaultMessage:
              'Why are teams choosing {br} Slite over Google Docs?',
            id: 'compare.googleDocs.featureSection.title',
          },
          { br: <br className="display-none-small" /> }
        )}
        benefits={[
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/eb3db30de3a651a1f12def6d7d4e8c182552db92-1019x922.png?auto=format&q=75&dpr=2',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Organization illustration',
              id: 'compare.googleDocs.feature1.image.alt',
            }),
            imageBackground: 'saturn-orange-lighter',
            title: intl.formatMessage({
              defaultMessage: 'Better organization.',
              id: 'compare.googleDocs.feature1.title',
            }),
            description: intl.formatMessage({
              id: 'compare.googleDocs.feature1.description',
              defaultMessage:
                "Channels, not folders. We've all been there: you create a Google Doc, share it, and a few days later it's impossible to find in Drive. In Slite, docs live in easy-to-find channels, so knowledge is always a few clicks away.",
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/94139512ad92efef586ef8eb19e9529eccac3e19-800x619.png?auto=format&q=75&dpr=2',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Team catch-up illustration',
              id: 'compare.googleDocs.feature2.image.alt',
            }),
            imageBackground: 'pink-moon',
            title: intl.formatMessage({
              defaultMessage: "Know what's happening.",
              id: 'compare.googleDocs.feature2.title',
            }),
            description: intl.formatMessage({
              id: 'compare.googleDocs.feature2.description',
              defaultMessage:
                "Google Docs gives teams little context on what people are working on. Slite is built around collaboration and getting your team in sync, with clear signals on what's new, what's being updated, and what matters.",
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/2789278e652e7ced21b78f06135061c88b8b6dcf-1382x1200.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Editor illustration',
              id: 'compare.googleDocs.feature3.image.alt',
            }),
            imageBackground: 'primary',
            title: intl.formatMessage({
              defaultMessage: 'Search that just works.',
              id: 'compare.googleDocs.feature3.title',
            }),
            description: intl.formatMessage({
              id: 'compare.googleDocs.feature3.description',
              defaultMessage:
                "For a tool that's intended to boost productivity, search shouldn't get in the way. With Slite, enjoy lightning-fast search if you already know a doc you're looking for or just ask our AI assistant and get the answer in an instant",
            }),
          },
        ]}
      />

      <SectionCurve from="shade-100" to="pink-moon-darker" shape="top-large" />
      <SectionQuote
        background="pink-moon-darker"
        quote={intl.formatMessage({
          defaultMessage:
            '"All the important information at Pumpkin is now finally in one place. It\'s literally keeping the team on the same page: previously on Google Docs, no one came to check what work others were up to. We also avoid losing valuable insights in our Slack history."',
          id: 'compare.googleDocs.quote.hugo.text',
        })}
        authorImageUrl="https://cdn.sanity.io/images/dnsgqii5/production/1c7a6541d434fb0f36443cf96286188545073fbc-500x500.webp"
        authorTitle="Hugo Sallé de Chou"
        authorDescription="Co-founder, PUMPKIN"
      />
      <SectionCurve
        from="shade-100"
        to="pink-moon-darker"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionGridBenefits
        title={intl.formatMessage({
          defaultMessage: 'A calmer, clearer way to work with docs.',
          id: 'compare.googleDocs.benefitsSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage: 'How will I know if a doc is up to date?',
              id: 'compare.googleDocs.grid.benefit1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Use doc verification status and let your team know what they can trust straight away.',
              id: 'compare.googleDocs.grid.benefit1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/cb7129111fa7ce083b3be2b2215303a6ce3fe23b-1375x852.jpg',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Doc verification status illustration',
              id: 'compare.googleDocs.grid.benefit1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'Yes, but it will still get outdated at some point...',
              id: 'compare.googleDocs.grid.benefit2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Choose different validity periods and get automatic reminders when your docs have to be reviewed.',
              id: 'compare.googleDocs.grid.benefit2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/9c6a09e0aaca7994ab26260d26050eddc1a06820-1375x1120.png',
            imageClassnames: 'padding-horizontal-24',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Doc review reminder illustration',
              id: 'compare.googleDocs.grid.benefit2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'Are decisions also knowledge?',
              id: 'compare.googleDocs.grid.benefit3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Get into the habit of documenting important questions and answers in one place. Use our dedicated space for structured decision-making, involve people that matter and set deadlines.',
              id: 'compare.googleDocs.grid.benefit3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/3272fd655050553d144e9d7e11f00165f232c0b2-1375x1120.png',
            imageClassnames: 'flexbox flexbox-justify-end',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Decision-making illustration',
              id: 'compare.googleDocs.grid.benefit3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "Will switching to Slite end my team's complaints about the tool being too technical?",
              id: 'compare.googleDocs.faq4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Yes, because Slite is designed to feel familiar, not heavier. Teams coming from Google Docs find it easy to start writing right away, while benefiting from clearer structure, better visibility, and fewer lost documents. That combination makes Slite stick, instead of becoming yet another tool people forget to check.',
              id: 'compare.googleDocs.faq4.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/569a7178bcccf8f49b9775eae3c5e4a6fe7e1a78-1072x665.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Easy for everyone illustration',
              id: 'compare.googleDocs.faq4.image.alt',
            }),
          },
        ]}
      />

      <SectionCurve from="shade-100" to="shade-00" shape="top-large" />
      <SectionTable
        competitorName="Google Docs"
        title={intl.formatMessage({
          defaultMessage: 'A document editor is not a knowledge base.',
          id: 'compare.googleDocs.comparisonTable.title',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Google Docs has no verification, no AI answers, no MCP, and no way to know what is outdated. Slite is built from the ground up to manage team knowledge.',
          id: 'compare.googleDocs.comparisonTable.description.v2',
        })}
        checkedFeatures={['company-wiki-templates']}
      />
      <SectionCurve
        from="moon-dust"
        to="shade-00"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionTestimonials withCaseStudies={false} />
      <SectionLastCTA background="moon-dust" />
    </SlitePage>
  )
}
